import { computed, ref } from 'vue'
import { projectsApi } from '@/api/projects'
import { withAsyncState } from '@/lib/asyncState'
import { pollUntil } from '@/lib/poll'
import type { LinearTeam, LinearTeamsFetch } from '@/types/models'

const POLL_INTERVAL_MS = 2_000
const POLL_TIMEOUT_MS = 60_000

// Singleton: the create and edit project forms both need this list, and it
// changes rarely enough that fetching it once per session is enough.
const teams = ref<LinearTeam[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const initialized = ref(false)
let fetchPromise: Promise<void> | null = null

export function useLinearTeams() {
  async function fetch(force = false): Promise<void> {
    if (initialized.value && !force) return
    if (fetchPromise && !force) return fetchPromise

    fetchPromise = withAsyncState(
      error,
      'Failed to load Linear teams.',
      async () => {
        const started = await projectsApi.startLinearTeamsFetch(force)

        // The endpoint always queues a fresh fetch and returns 'pending'
        // immediately (see StartLinearTeamsFetchAction) — poll the status
        // endpoint until the job resolves it.
        const resolved: LinearTeamsFetch | undefined =
          started.status === 'pending'
            ? await pollUntil(
                async () => {
                  const polled = await projectsApi.getLinearTeamsFetch(started.id)
                  return polled.status === 'pending' ? undefined : polled
                },
                { intervalMs: POLL_INTERVAL_MS, timeoutMs: POLL_TIMEOUT_MS },
              )
            : started

        if (!resolved || resolved.status === 'failed') {
          error.value = resolved?.error_message ?? 'Failed to load Linear teams.'
          return
        }

        teams.value = resolved.teams ?? []
        initialized.value = true
      },
      loading,
    ).then(() => {
      fetchPromise = null
    })

    return fetchPromise
  }

  // Called on logout: `initialized` must be cleared too, not just the data,
  // or the next session's `fetch()` short-circuits and never hits the
  // network — leaving the previous user's teams visible indefinitely.
  function reset(): void {
    teams.value = []
    loading.value = false
    error.value = null
    initialized.value = false
    fetchPromise = null
  }

  return {
    teams: computed(() => teams.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    fetch,
    reset,
  }
}
